/**
 * PromoBanner — Renders a promotional banner for the Beta BOGO campaign.
 *
 * Displays above the entitlements card when the user has an active, unredeemed
 * promo code. Fetches promo status on mount using stale-while-revalidate caching
 * (via promoService). Does not block the critical rendering path — shows a loading
 * skeleton or renders after main dashboard content.
 *
 * Does not render when:
 * - Promo is redeemed
 * - Promo is expired
 * - No active promo exists
 * - Fetch fails (fail-closed for UI)
 *
 * @see Requirements 7.1, 7.2, 7.3, 7.4
 */

import { useState, useEffect } from 'react';
import { Gift, Tag } from 'lucide-react';

import { promoService } from '../../../../services/promoService';
import type { PromoStatusResponse } from '../../../../services/promoService';

// --- Types ---

interface PromoState {
  status: 'loading' | 'active' | 'hidden';
  promoCode: string | null;
  expiresAt: string | null;
}

// --- Constants ---

const PROMO_EXPIRY_DATE = '2026-06-30T23:59:59Z';
const PROMO_DISPLAY_EXPIRY = 'June 30, 2026';

// --- Helpers ---

function isPromoExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return true;
  return new Date() > new Date(expiresAt);
}

function shouldShowBanner(data: PromoStatusResponse['data']): boolean {
  if (!data.hasActivePromo) return false;
  if (data.redeemed) return false;
  if (isPromoExpired(data.expiresAt ?? PROMO_EXPIRY_DATE)) return false;
  return true;
}

// --- Component ---

export function PromoBanner() {
  const [promoState, setPromoState] = useState<PromoState>({
    status: 'loading',
    promoCode: null,
    expiresAt: null,
  });

  useEffect(() => {
    const controller = new AbortController();

    async function fetchPromoStatus() {
      try {
        const response = await promoService.getStatus(controller.signal);

        if (controller.signal.aborted) return;

        if (shouldShowBanner(response.data)) {
          setPromoState({
            status: 'active',
            promoCode: response.data.promoCode,
            expiresAt: response.data.expiresAt,
          });
        } else {
          setPromoState({ status: 'hidden', promoCode: null, expiresAt: null });
        }
      } catch {
        // Fail-closed: don't render banner on error
        if (!controller.signal.aborted) {
          setPromoState({ status: 'hidden', promoCode: null, expiresAt: null });
        }
      }
    }

    fetchPromoStatus();

    return () => {
      controller.abort();
    };
  }, []);

  // --- Loading skeleton ---
  if (promoState.status === 'loading') {
    return (
      <div
        aria-hidden="true"
        className="animate-pulse rounded-xl border border-purple-500/20 bg-purple-950/10 p-5"
        data-testid="promo-banner-skeleton"
      >
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-full bg-purple-500/20" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-4 w-48 rounded bg-purple-500/15" />
            <div className="h-3 w-64 rounded bg-purple-500/10" />
          </div>
        </div>
      </div>
    );
  }

  // --- Hidden state: don't render ---
  if (promoState.status === 'hidden') {
    return null;
  }

  // --- Active promo banner ---
  return (
    <div
      className="rounded-xl border border-purple-500/20 bg-purple-950/20 p-5 transition-all"
      data-testid="promo-banner"
      role="region"
      aria-label="Promotional offer"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {/* Left: Icon + Text */}
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-purple-500/15">
            <Gift className="text-purple-400" aria-hidden="true" />
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="typography-card-title text-purple-100">
              Buy One Get One Free
            </h3>
            <p className="typography-body text-purple-300/80">
              Purchase one manual subscription and get a second one free.
              Expires {PROMO_DISPLAY_EXPIRY}.
            </p>
          </div>
        </div>

        {/* Right: Promo code badge */}
        <div className="flex items-center gap-2 sm:shrink-0">
          <div
            className="promo-code-pill flex items-center gap-1.5 rounded-full px-3 py-1.5"
            style={{
              background: 'linear-gradient(90deg, #FD936E 0%, #8181FF 100%)',
            }}
          >
            <Tag className="text-white" aria-hidden="true" />
            <span className="typography-tag font-bold text-white">
              {promoState.promoCode ?? 'BETA-BOGO-2026'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
